//#region react
import React, { useEffect, useState, useMemo, useCallback } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedTaskToView } from "_redux/slices/timeline/selected-task";
import { selectIata } from "_redux/slices/main-screen/user";
import { selectFlightById, selectFlightFromDBById, selectFlightsByTime, getFlightById, resetGetFlightFromDBById } from '_redux/slices/timeline/timeline-flight-list';
import { selectAllBookingsByFlight } from '_redux/slices/timeline/timeline-bookings';
import { selectTasksByFlight } from '_redux/slices/timeline/timeline-data';
import {
    getFlightPassengerList,
    selectFlightPassengerList,
    selectFlightPassengerListStatus,
    reset as resetFlightInformation
} from '_redux/slices/timeline/timeline-flight-information';

// styles 
import { Spacing, Colors } from '_styles'

// functions
import { checkBookingOrTaskProperties, getIataTimezone } from '_utils/functions';

// icons
import { FiRefreshCcw } from "react-icons/fi";

// bootstrap
import { Button } from 'react-bootstrap';

// components
import { CustomSpinner } from '_components/atoms';
import FlightInformationCard from './flight-information-card';
import ViewFlightPaxList from './view-flight-pax-list';
import TabSwitcherSmall from '_components/atoms/tab-switcher-small';
import TaskInformationCard from './task-information-card';

//#endregion


const ViewJob = ({ onUpdate }) => {

    //#region local states
    const dispatch = useDispatch();
    const iata = useSelector(selectIata)
    // task info / type
    const [type, setType] = useState("");                        // job / task / alt pickup / ramp task / boarding / flight task
    const [isTask, setIsTask] = useState(false);                // true if viewing task not booking
    const [flightKey, setFlightKey] = useState(null);           // flight key for fetching bookings
    const [lastUpdated, setLastUpdated] = useState(null);
    const [flightDocument, setFlightDocument] = useState(null);

    // tab state
    const [activeTab, setActiveTab] = useState("task-information");

    // tab change handler
    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
    };

    //#endregion

    //#region redux
    const taskToView = useSelector(selectSelectedTaskToView);
    const isRC = !!(taskToView?.paxManualCheckIn || taskToView?.originalBookingDoc?.paxManualCheckIn);
    const rcBookingId = taskToView?._id?.includes('_IATA_')
        ? taskToView._id.split('_IATA_')[0]
        : (taskToView?.originalBookingId || taskToView?._id);
    const mainFlightDocument = useSelector(selectFlightById(flightKey));
    const flightFromDBById = useSelector(selectFlightFromDBById);

    const bookingsForFlight = useSelector(selectAllBookingsByFlight(flightKey));
    const tasksForFlight = useSelector(selectTasksByFlight(flightDocument?.flightNumber));            // tasks for the flight
    const flightsByTime = useSelector(selectFlightsByTime);

    // Flight passenger list from dedicated API (bypasses zone/date filters)
    const fetchedPassengerList = useSelector(selectFlightPassengerList);
    const fetchStatus = useSelector(selectFlightPassengerListStatus);
    //#endregion

    //#region API fetch helpers

    // Helper to get flight params for API call
    const getFlightParams = useCallback(() => {
        if (!taskToView) return null;

        let flightNumber, departureDate, apiType;

        if (isTask) {
            // For tasks, extract from flightInformation
            const flightInfo = taskToView?.flightInformation;
            if (!flightInfo) return null;

            flightNumber = flightInfo?.flightNumber;
            departureDate = flightDocument?.flightDate || flightDocument?.scheduledDate || flightInfo?.scheduledDate || flightInfo?.flightDate || flightInfo?.date;
            const isInbound = flightInfo?.arrivalAirport === iata;


            apiType = isInbound ? 'inbound' : 'outbound';

        } else {
            // For bookings, extract from flightConfirmationDetails
            const flightDetails = taskToView?.flightConfirmationDetails;
            if (!flightDetails) return null;

            flightNumber = flightDetails?.carrierCode + flightDetails?.flightNumber;

            // departureDate = flightDocument?.flightDate || flightDocument?.scheduledDate || flightDetails?.departureDate;
            departureDate = flightDocument?.flightDate || flightDocument?.scheduledDate

            // Determine if inbound or outbound based on airport matching
            const isInbound = flightDetails?.destination === iata;
            apiType = isInbound ? 'inbound' : 'outbound';

        }

        if (flightNumber && departureDate && iata) {

            return { iata, flightNumber, departureDate, type: apiType };
        }
        return null;
    }, [taskToView, isTask, iata, flightDocument]);

    // Fetch passenger list from server
    const fetchPassengerList = useCallback(() => {
        const params = getFlightParams();
        if (params) {
            dispatch(getFlightPassengerList(params));
        }
    }, [dispatch, getFlightParams]);

    // Refresh handler for manual refresh
    const handleRefresh = useCallback(() => {
        fetchPassengerList();
    }, [fetchPassengerList]);


    useEffect(() => {
        dispatch(resetGetFlightFromDBById());
    }, [])

    // set flight document based on main flight document if exists or fetch from db if not
    useEffect(() => {
        // main flight doc set by selectFlightById (local state)
        if (mainFlightDocument) {
            // exists, used it
            setFlightDocument(mainFlightDocument);
        }
        else if (flightKey && !mainFlightDocument) {

            // flight does not exist in local state, take flight id and query db for flight document using selectFlightFromDBById (redux state)
            // make sure id matches the id for querying flight by id
            const finalFlightId = `${flightKey}_IATA_${iata}`;
            if (finalFlightId) dispatch(getFlightById(finalFlightId));

        }
    }, [mainFlightDocument, flightKey]);

    // set flight document based on flight from db if exists
    useEffect(() => {

        // check for flight info from db
        if (flightFromDBById) {

            // if flight from db exists and main flight document does not exist, set flight document to flight from db
            if (flightFromDBById && !mainFlightDocument) {

                // get scheduled date
                const scheduledDate = flightFromDBById?.originalFlightTime
                    ? typeof flightFromDBById?.originalFlightTime === 'string'
                        ? flightFromDBById?.originalFlightTime?.split('T')?.[0]
                        : new Date(flightFromDBById?.originalFlightTime)?.toISOString()?.split('T')?.[0]
                    : '';

                // get flight date
                const flightDate = flightFromDBById?.operationInfo?.time
                    ? typeof flightFromDBById?.operationInfo.time === 'string'
                        ? flightFromDBById?.operationInfo?.time?.split('T')?.[0]
                        : new Date(flightFromDBById?.operationInfo.time)?.toISOString()?.split('T')?.[0]
                    : '';

                // check info is correct based on booking / task info

                // set flight document to flight from db
                setFlightDocument({
                    ...flightFromDBById,
                    flightId: flightFromDBById?._id ?? null,
                    bookingKey: `${flightFromDBById?.flightNumber}_${flightFromDBById?.departureAirport}_${flightFromDBById?.arrivalAirport}_${scheduledDate}`,
                    flightDate: flightDate,
                    status: flightFromDBById?.flightStatus == 'X' || flightFromDBById?.flightStatusCodeCDM == 'CNL' ? 'cancelled' : flightFromDBById?.operationInfo?.status,
                    statusType: flightFromDBById?.operationInfo?.statusType,
                    airline: flightFromDBById.flightNumber.slice(0, 2),
                    origin: flightFromDBById?.departureAirport,
                    destination: flightFromDBById?.arrivalAirport,
                    scheduled: flightFromDBById?.originalFlightTime,
                    operationTime: flightFromDBById?.operationInfo?.time,
                    cancelled: flightFromDBById?.flightStatus == 'X' || flightFromDBById?.flightStatusCodeCDM == 'CNL' ? true : false,
                    flightFeedTime: flightFromDBById?.flightFeedTime ?? flightFromDBById?.operationInfo?.time ?? null,
                    scheduledDate: flightFromDBById?.scheduledDate || scheduledDate

                });

            }
            else {
                // if main flight document exists, set flight document to main flight document
                setFlightDocument(mainFlightDocument);
            }
        }
    }, [flightFromDBById, mainFlightDocument]);

    // Update last updated timestamp when fetch succeeds
    useEffect(() => {
        if (fetchStatus === 'succeeded') {
            setLastUpdated(new Date());
        }
    }, [fetchStatus]);

    // Fetch passenger list from server when task/booking changes
    useEffect(() => {
        if (taskToView && flightKey && !taskToView?.isBreakTask && !taskToView?.isAdhocTask) {
            fetchPassengerList();
        }

        // Cleanup on unmount
        return () => {
            dispatch(resetFlightInformation());
        };
    }, [dispatch, taskToView, flightKey, fetchPassengerList]);

    //#endregion

    //#region useMemo - Create passenger flight list
    // update active tab when isTask changes or for break tasks
    useEffect(() => {
        if (taskToView?.isBreakTask || taskToView?.isAdhocTask) {
            setActiveTab("task-information");
        }
        // Switch to passenger info if task tab was active but no longer available
        else if (!isTask && activeTab === "task-information") {
            setActiveTab("passenger-information");
        }
    }, [isTask, activeTab, taskToView?.isBreakTask]);

    // Fallback: Combine bookings and tasks manually from cached Redux data
    const cachedPassengerList = useMemo(() => {
        let paxList = [];

        if (taskToView?.isBreakTask || taskToView?.isAdhocTask) return [];

        // add bookings to pax list
        if (bookingsForFlight && bookingsForFlight.length > 0) {
            paxList = paxList.concat(bookingsForFlight);
        }

        // for each booking, check if any tasks exist and pair them
        paxList = paxList.map(booking => {

            // find matching tasks using id
            const passengerTasks = tasksForFlight?.filter(task => task.originalBookingId === booking.originalBookingId) || [];

            // find ramp tasks
            let rampTasks = tasksForFlight?.filter(task => task.combinedRampBooking === true) || [];

            let response = { ...booking }

            if (passengerTasks && passengerTasks.length > 0) {
                // add tasks to booking
                response = {
                    ...response,
                    tasks: passengerTasks,
                }
            }

            if (rampTasks && rampTasks.length > 0) {
                // add ramp tasks to booking
                response = {
                    ...response,
                    rampTasks: rampTasks,
                }
            };

            // if no matching task, return booking as is
            return response;

        });

        return paxList;

    }, [bookingsForFlight, tasksForFlight, taskToView?.isBreakTask]);

    // Use fetched data if available (includes tasks from backend), 
    // otherwise fall back to cached data (manually combined)
    const passengerFlightList = useMemo(() => {
        if (fetchStatus === 'succeeded' && fetchedPassengerList.length > 0) {
            // Backend returns bookings with tasks already combined
            return fetchedPassengerList;
        }
        // Fall back to cached bookings while loading or if fetch returns empty
        return cachedPassengerList;
    }, [fetchStatus, fetchedPassengerList, cachedPassengerList]);

    //#endregion

    // tabs configuration - only show task information tab for break tasks, otherwise show all tabs
    const tabs = (taskToView?.isBreakTask || taskToView?.isAdhocTask)
        ? [{ value: "task-information", label: "Task Information" }]
        : [
            ...(isTask ? [{ value: "task-information", label: "Task Information" }] : []),
            { value: "passenger-information", label: "Passenger Information" },
            { value: "flight-information", label: "Flight Information" },
        ];

    //#region useEffect

    // task or booking listener
    useEffect(() => {
        if (taskToView) {
            const { isTask, jobType } = checkBookingOrTaskProperties(taskToView, iata);
            //console.log('task to view ', taskToView);
            //console.log('job type ', jobType);
            setType(jobType);
            setIsTask(isTask);
        }
    }, [taskToView]);

    // get flight key for fetching bookings (similar to view-flight.js)
    useEffect(() => {
        if (taskToView) {

            //console.log('taskToView:', taskToView);

            if (isTask) {
                // For tasks, extract flight key from flightInformation
                const flightInfo = taskToView?.flightInformation;

                //console.log('flightInfo:', flightInfo);

                if (flightInfo) {
                    const flightDate = flightInfo?.flightDate || flightInfo?.departureDate || flightInfo?.flightDate || flightInfo?.date;
                    let key = flightInfo?.flightNumber + '_' + flightInfo?.departureAirport + '_' + flightInfo?.arrivalAirport + '_' + flightDate;

                    //console.log('key:', key);
                    setFlightKey(key);
                }
            } else {
                // For bookings, extract flight key from flightConfirmationDetails
                const flightDetails = taskToView?.flightConfirmationDetails;
                if (flightDetails) {
                    const isArrival = flightDetails?.destination === iata;
                    // const flightDate = isArrival
                    //     ? flightDetails?.arrivalTime?.split("T")?.[0]
                    //     : flightDetails?.departureTime?.split("T")?.[0];
                    let flightDate = "";
                    if (isArrival) {
                        flightDate = new Date(flightDetails?.arrivalTime)?.toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
                    } else {
                        flightDate = flightDetails?.departureDate ? flightDetails?.departureDate : new Date(flightDetails?.departureTime)?.toLocaleDateString('en-CA', { timeZone: getIataTimezone(iata) });
                    }
                    let key = flightDetails?.carrierCode + flightDetails?.flightNumber + '_' + flightDetails?.origin + '_' + flightDetails?.destination + '_' + flightDate;
                    setFlightKey(key);
                }
            }
        }
    }, [taskToView, isTask]);

    // useEffect(() => {
    //     console.log('flightDocument:', flightDocument);
    // }, [flightDocument])

    // useEffect(() => {
    //     console.log('flightsByTime:', flightsByTime);
    // }, [flightsByTime])

    //#endregion

    return (
        <div style={{ marginBottom: Spacing.SCALE_16, marginTop: -Spacing.SCALE_22 }}>

            {/* Tab Switcher */}
            <TabSwitcherSmall
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                containerStyle={{}}
                tabsListStyle={{
                    marginBottom: Spacing.SCALE_24,
                }}
                tabContentStyle={{}}
            >
                {/* Task Information Tab */}
                {activeTab === "task-information" && (
                    <div>
                        {/* Task Information - only show for tasks */}
                        {isTask && (
                            <TaskInformationCard
                                task={taskToView}
                                iata={iata}
                            />
                        )}
                    </div>
                )}

                {/* Passenger Information Tab */}
                {activeTab === "passenger-information" && (
                    <>
                        {/* Refresh toolbar */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'flex-end',
                            flexDirection: 'column',
                            marginBottom: Spacing.SCALE_16
                        }}>
                            {/* Refresh button */}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={fetchStatus === 'loading'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: Spacing.SCALE_8
                                }}
                            >
                                {fetchStatus === 'loading' ? (
                                    <CustomSpinner size="small" />
                                ) : (
                                    <FiRefreshCcw size={14} />
                                )}
                                <span>Refresh</span>
                            </Button>

                            {/* Last updated timestamp */}
                            <span style={{
                                fontSize: 12,
                                color: Colors.GRAY_MEDIUM,
                                marginTop: Spacing.SCALE_4,
                            }}>
                                {lastUpdated ? (
                                    `Last updated: ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                ) : (
                                    'Loading...'
                                )}
                            </span>
                        </div>

                        {/* Passenger list */}
                        {fetchStatus === 'loading' && !cachedPassengerList.length ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: Spacing.SCALE_24 }}>
                                <CustomSpinner />
                            </div>
                        ) : (
                            !taskToView?.isBreakTask && (
                                <ViewFlightPaxList
                                    selectedFlightToView={{
                                        ...flightDocument,
                                        type: type, // pass the type for proper status column headers
                                    }}
                                    passengerFlightList={passengerFlightList}
                                    flightType={type}
                                    onRefresh={handleRefresh}
                                />
                            )
                        )}
                    </>
                )}

                {/* Flight Information Tab */}
                {activeTab === "flight-information" && (
                    <div>
                        {/* Flight Information */}
                        {!taskToView?.isBreakTask && (
                            <FlightInformationCard flightDocument={flightDocument} onUpdate={onUpdate} bookingId={rcBookingId} isRC={isRC} />
                        )}
                    </div>
                )}

            </TabSwitcherSmall>
        </div>
    );
};

export default ViewJob;